import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, FileSignature, Camera, ScrollText, Shield, Users, Save, LinkIcon, Heart, ArrowLeft,
} from "lucide-react";
import Footer from "@/components/Footer";
import BrandLogo from "@/components/BrandLogo";

// Static changelog page — accessible from the app footer.
export default function Changelog() {
  return (
    <div className="min-h-screen bg-secondary/30 flex flex-col" data-testid="changelog-page">
      {/* Slim header with back-home link */}
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 font-serif-heading text-neutral-800 hover:text-primary transition-colors"
            data-testid="changelog-home-link"
          >
            <BrandLogo size={22} />
            AI Bridal Manager
          </Link>
          <Link
            to="/"
            className="text-xs text-neutral-500 hover:text-primary inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Home
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
          <div className="text-center">
            <span className="label-uppercase text-primary">Changelog</span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-3">
              Aggiornamenti
            </h1>
            <p className="text-sm sm:text-base text-neutral-600 mt-3">
              La storia delle evoluzioni di AI Bridal Manager. Ogni release pensata per
              farti gestire meglio le tue spose.
            </p>
          </div>

          {/* Versione 1.1 */}
          <article
            className="mt-14 bg-white border border-border rounded-sm p-6 sm:p-10"
            data-testid="changelog-v1-1"
          >
            <header className="flex items-baseline justify-between flex-wrap gap-2 pb-5 border-b border-border">
              <div>
                <span className="label-uppercase text-primary">Versione 1.1</span>
                <h2 className="font-serif-heading text-3xl mt-2 tracking-tight">
                  Evoluzione operativa
                </h2>
              </div>
              <span className="text-xs text-neutral-400 tabular-nums">Ultimo aggiornamento</span>
            </header>
            <p className="text-sm text-neutral-700 mt-5">
              Aggiornamento focalizzato su <strong>automazione</strong>, <strong>personalizzazione</strong> e
              <strong> gestione avanzata del team</strong>.
            </p>

            <div className="mt-8 space-y-7">
              <Block icon={Save} title="Automazioni & gestione clienti">
                <FeatureRow title="Salvataggio automatico schede cliente">
                  Nessuna perdita dati durante la compilazione: ogni modifica viene salvata in tempo reale.
                </FeatureRow>
                <FeatureRow icon={LinkIcon} title="Link profilazione primo contatto">
                  Invia alla cliente un link personalizzato attraverso il quale può inserire autonomamente tutte le
                  informazioni necessarie (data evento, location, servizi richiesti, note, ecc.). I dati vengono
                  raccolti in modo strutturato e ti permettono di:
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-neutral-600">
                    <li>preparare un preventivo preciso;</li>
                    <li>gestire il primo contatto in modo professionale;</li>
                    <li>risparmiare tempo nella fase di consulenza iniziale.</li>
                  </ul>
                </FeatureRow>
              </Block>

              <Block icon={Heart} title="Gestione sposa & organizzazione evento">
                <FeatureRow title="Orario uscita sposa">
                  Inserimento e gestione del timing per il giorno dell'evento, condiviso automaticamente con tutto il team.
                </FeatureRow>
              </Block>

              <Block icon={Camera} title="Media & documenti">
                <FeatureRow title="Condivisione foto con PDF personalizzato">
                  Invia prove trucco/capelli con allegato PDF professionale, tramite link privato cliente
                  (WhatsApp, email o link diretto).
                </FeatureRow>
              </Block>

              <Block icon={ScrollText} title="Contratti">
                <FeatureRow title="Contratto personalizzato">
                  Nuova sezione impostazioni con scelta tra modello standard e personalizzato, applicabile
                  istantaneamente a ogni cliente.
                </FeatureRow>
              </Block>

              <Block icon={Shield} title="Sicurezza">
                <FeatureRow title="Cambio password da impostazioni">
                  Gestione autonoma delle credenziali con verifica della password attuale e mostra/nascondi sicuro.
                </FeatureRow>
              </Block>

              <Block icon={Users} title="Gestione team wedding">
                <FeatureRow title="Gestione collaboratori per servizi sposa">
                  Aggiungi membri del team con colore dedicato per il calendario.
                </FeatureRow>
                <FeatureRow title="Visualizzazione nel calendario">
                  Ogni evento mostra automaticamente operatori trucco e capelli con il loro colore.
                </FeatureRow>
                <FeatureRow title="Assegnazione ruoli">
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-neutral-600">
                    <li>Hairstylist principale;</li>
                    <li>Makeup artist principale;</li>
                    <li>Assistenti multipli;</li>
                  </ul>
                  Configurazione direttamente nelle schede Makeup/Hair, con filtro operatori sul calendario.
                </FeatureRow>
              </Block>
            </div>
          </article>

          {/* Versione 1.0 */}
          <article
            className="mt-10 bg-white border border-border rounded-sm p-6 sm:p-10"
            data-testid="changelog-v1-0"
          >
            <header className="flex items-baseline justify-between flex-wrap gap-2 pb-5 border-b border-border">
              <div>
                <span className="label-uppercase text-primary">Versione 1.0</span>
                <h2 className="font-serif-heading text-3xl mt-2 tracking-tight">Release iniziale</h2>
              </div>
              <span className="text-xs text-neutral-400 tabular-nums">Gennaio 2026</span>
            </header>
            <p className="text-sm text-neutral-700 mt-5">
              Lancio ufficiale della piattaforma <strong>AI Bridal Manager</strong>, progettata per
              semplificare la gestione completa delle spose e dei servizi wedding.
            </p>

            <div className="mt-7 space-y-3">
              <BulletRow icon={Sparkles}>Creazione e gestione schede clienti spose</BulletRow>
              <BulletRow icon={FileSignature}>Gestione appuntamenti e calendario servizi</BulletRow>
              <BulletRow icon={Camera}>Inserimento prove trucco e capelli</BulletRow>
              <BulletRow icon={Save}>Organizzazione base dei dati cliente</BulletRow>
              <BulletRow icon={Users}>Interfaccia operativa per professionisti beauty</BulletRow>
            </div>
          </article>

          <p className="text-center text-xs text-neutral-400 mt-12">
            Hai un suggerimento? Scrivici, costruiamo AI Bridal Manager insieme.
          </p>
        </div>
      </main>

      <Footer variant="default" />
    </div>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2">
        {Icon && (
          <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
          </span>
        )}
        <h3 className="font-serif-heading text-xl tracking-tight">{title}</h3>
      </div>
      <div className="mt-4 space-y-4 pl-10">{children}</div>
    </section>
  );
}

function FeatureRow({ icon: Icon, title, children }) {
  return (
    <div className="text-sm">
      <div className="font-medium text-neutral-800 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />}
        {title}
      </div>
      <div className="text-neutral-600 mt-1 leading-relaxed">{children}</div>
    </div>
  );
}

function BulletRow({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-3 text-sm text-neutral-700">
      <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
      </span>
      <span className="leading-relaxed pt-1">{children}</span>
    </div>
  );
}
